import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BookSelector } from '@/components/BookSelector';
import { AIAwardShortlistSuggestion } from '@/components/AIAwardShortlistSuggestion';
import { BulkImportAwardBooks } from '@/components/admin/BulkImportAwardBooks';
import { BulkCreateAwards } from '@/components/admin/BulkCreateAwards';
import { AwardShortlistBook } from '@/hooks/useAwardShortlistSuggestion';
import { toast } from '@/hooks/use-toast';
import { Loader2, Plus, X, Trophy, Crown } from 'lucide-react';

const PRIZE_OPTIONS = [
  'The Booker Prize',
  'Women\'s Prize for Fiction',
  'Baillie Gifford Prize',
  'International Booker Prize',
  'Pulitzer Prize for Fiction',
  'Pulitzer Prize for History',
  'Pulitzer Prize for Biography',
  'Pulitzer Prize for General Nonfiction',
  'Pulitzer Prize for Memoir',
  'National Book Award for Fiction',
  'National Book Award for Nonfiction',
  'National Book Award for Poetry',
  'National Book Award for Translated Literature',
  'National Book Award for Young People\'s Literature',
  'Dylan Thomas Prize',
  'Goldsmiths Prize',
  'Scotiabank Giller Prize',
  'Nobel Prize in Literature',
];

interface ShortlistBook {
  book_id: string;
  title: string;
  authors: string[];
  cover_url: string | null;
  position: number | null;
}

export default function AdminAwards() {
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const navigate = useNavigate();
  
  const [prizeName, setPrizeName] = useState('');
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [shortlistDate, setShortlistDate] = useState('');
  const [region, setRegion] = useState<'UK' | 'US' | 'Intl'>('UK');
  const [genreFocus, setGenreFocus] = useState<'fiction' | 'nonfiction' | 'translation' | 'lifetime'>('fiction');
  const [description, setDescription] = useState('');
  const [link, setLink] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [shortlistBooks, setShortlistBooks] = useState<ShortlistBook[]>([]);
  const [saving, setSaving] = useState(false);
  const [bookSelectorOpen, setBookSelectorOpen] = useState(false);
  const [existingAwards, setExistingAwards] = useState<Array<{ id: string; prize_name: string; year: number }>>([]);
  const [editingAwardId, setEditingAwardId] = useState<string | null>(null);
  const [loadingAward, setLoadingAward] = useState(false);

  useEffect(() => {
    const fetchAwards = async () => {
      const { data } = await supabase
        .from('book_awards')
        .select('id, prize_name, year')
        .order('year', { ascending: false });
      
      if (data) {
        setExistingAwards(data);
      }
    };
    
    if (isAdmin) {
      fetchAwards();
    }
  }, [isAdmin]);

  if (adminLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    navigate('/');
    return null;
  }

  const handleAddBook = (book: any) => {
    if (!book) return;
    
    if (shortlistBooks.some(b => b.book_id === book.id)) {
      toast({
        title: "Book already added",
        description: "This book is already in the shortlist",
        variant: "destructive",
      });
      return;
    }

    setShortlistBooks([
      ...shortlistBooks,
      {
        book_id: book.id,
        title: book.title,
        authors: book.authors || [book.author],
        cover_url: book.cover_url,
        position: shortlistBooks.length + 1,
      },
    ]);
  };

  const handleRemoveBook = (bookId: string) => {
    setShortlistBooks(shortlistBooks.filter(b => b.book_id !== bookId));
  };

  const handleSetWinner = (bookId: string) => {
    setShortlistBooks(prevBooks => 
      prevBooks.map(book => ({
        ...book,
        position: book.book_id === bookId ? 1 : (book.position === 1 ? null : book.position)
      }))
    );
  };

  const handleEditAward = async (awardId: string) => {
    setLoadingAward(true);
    try {
      const { data: award, error: awardError } = await supabase
        .from('book_awards')
        .select('*')
        .eq('id', awardId)
        .single();

      if (awardError) throw awardError;

      const { data: books, error: booksError } = await supabase
        .from('award_shortlist_books')
        .select('*, books(id, title, authors, cover_url)')
        .eq('award_id', awardId);

      if (booksError) throw booksError;

      setPrizeName(award.prize_name);
      setYear(award.year.toString());
      setShortlistDate(award.shortlist_date || '');
      setRegion(award.region as 'UK' | 'US' | 'Intl');
      setGenreFocus(award.genre_focus as 'fiction' | 'nonfiction' | 'translation' | 'lifetime');
      setDescription(award.description || '');
      setLink(award.link || '');
      setLogoUrl(award.logo_url || '');
      
      const formattedBooks: ShortlistBook[] = books.map(b => ({
        book_id: b.book_id,
        title: b.books.title,
        authors: b.books.authors || [],
        cover_url: b.books.cover_url,
        position: b.position,
      }));
      
      setShortlistBooks(formattedBooks);
      setEditingAwardId(awardId);

      toast({
        title: "Award loaded",
        description: "You can now edit the award details and books",
      });
    } catch (error: any) {
      toast({
        title: "Error loading award",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoadingAward(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingAwardId(null);
    setPrizeName('');
    setYear(new Date().getFullYear().toString());
    setShortlistDate('');
    setDescription('');
    setLink('');
    setLogoUrl('');
    setShortlistBooks([]);
  };

  const handleAIApproval = (aiPrizeName: string, aiYear: number, aiBooks: AwardShortlistBook[]) => {
    setPrizeName(aiPrizeName);
    setYear(aiYear.toString());
    
    const formattedBooks: ShortlistBook[] = aiBooks.map(book => ({
      book_id: book.bookId!,
      title: book.title,
      authors: [book.author],
      cover_url: book.coverUrl || null,
      position: book.position,
    }));
    
    setShortlistBooks(formattedBooks);
    
    toast({
      title: "AI Suggestions Applied",
      description: `${aiBooks.length} books added to the form. Review and save when ready.`,
    });
  };

  const handleSave = async () => {
    if (!prizeName || !year || shortlistBooks.length === 0) {
      toast({
        title: "Missing information",
        description: "Please fill in prize name, year, and add at least one book",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      if (editingAwardId) {
        // Update existing award
        const { error: awardError } = await supabase
          .from('book_awards')
          .update({
            prize_name: prizeName,
            year: parseInt(year),
            shortlist_date: shortlistDate || null,
            region,
            genre_focus: genreFocus,
            description: description || null,
            link: link || null,
            logo_url: logoUrl || null,
          })
          .eq('id', editingAwardId);

        if (awardError) throw awardError;

        // Delete existing shortlist books and re-insert
        const { error: deleteError } = await supabase
          .from('award_shortlist_books')
          .delete()
          .eq('award_id', editingAwardId);

        if (deleteError) throw deleteError;

        // Insert updated shortlist books
        const { error: booksError } = await supabase
          .from('award_shortlist_books')
          .insert(
            shortlistBooks.map(book => ({
              award_id: editingAwardId,
              book_id: book.book_id,
              year: parseInt(year),
              position: book.position,
            }))
          );

        if (booksError) throw booksError;

        toast({
          title: "Award updated!",
          description: `${prizeName} ${year} shortlist has been updated`,
        });

        // Refresh the awards list
        const { data } = await supabase
          .from('book_awards')
          .select('id, prize_name, year')
          .order('year', { ascending: false });
        
        if (data) {
          setExistingAwards(data);
        }

        setEditingAwardId(null);
      } else {
        // Insert new award
        const { data: award, error: awardError } = await supabase
          .from('book_awards')
          .insert({
            prize_name: prizeName,
            year: parseInt(year),
            shortlist_date: shortlistDate || null,
            region,
            genre_focus: genreFocus,
            description: description || null,
            link: link || null,
            logo_url: logoUrl || null,
          })
          .select()
          .single();

        if (awardError) throw awardError;

        // Insert shortlist books
        const { error: booksError } = await supabase
          .from('award_shortlist_books')
          .insert(
            shortlistBooks.map(book => ({
              award_id: award.id,
              book_id: book.book_id,
              year: parseInt(year),
              position: book.position,
            }))
          );

        if (booksError) throw booksError;

        toast({
          title: "Award created!",
          description: `${prizeName} ${year} shortlist has been added`,
        });

        // Add to existing awards list
        setExistingAwards([award, ...existingAwards]);
      }

      // Reset form
      setPrizeName('');
      setYear(new Date().getFullYear().toString());
      setShortlistDate('');
      setDescription('');
      setLink('');
      setLogoUrl('');
      setShortlistBooks([]);
    } catch (error: any) {
      console.error('Error saving award:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save award",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
          <Trophy className="w-8 h-8" />
          Award Shortlists Admin
        </h1>
        <p className="text-muted-foreground">
          Add and manage book award shortlists
        </p>
      </div>

      <div className="space-y-6">
        {existingAwards.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Existing Awards</CardTitle>
              <CardDescription>Select an award to edit</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {existingAwards.map((award) => (
                  <div
                    key={award.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                  >
                    <div>
                      <p className="font-medium">{award.prize_name}</p>
                      <p className="text-sm text-muted-foreground">{award.year}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditAward(award.id)}
                      disabled={loadingAward}
                    >
                      {loadingAward ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Edit'
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <BulkCreateAwards />
        
        <BulkImportAwardBooks awards={existingAwards} />
        
        <AIAwardShortlistSuggestion onApprove={handleAIApproval} />

        <Card>
        <CardHeader>
          <CardTitle>
            {editingAwardId ? 'Edit Award Shortlist' : 'Create New Award Shortlist'}
          </CardTitle>
          <CardDescription>
            {editingAwardId 
              ? 'Update the award details and manage the shortlisted books'
              : 'Fill in the award details and select the shortlisted books'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="prize">Prize Name *</Label>
              <Select value={prizeName} onValueChange={setPrizeName}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a prize" />
                </SelectTrigger>
                <SelectContent>
                  {PRIZE_OPTIONS.map(prize => (
                    <SelectItem key={prize} value={prize}>
                      {prize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="year">Year *</Label>
              <Input
                id="year"
                type="number"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                placeholder="2024"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="shortlist-date">Shortlist Date</Label>
              <Input
                id="shortlist-date"
                type="date"
                value={shortlistDate}
                onChange={(e) => setShortlistDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="region">Region</Label>
              <Select value={region} onValueChange={(v: any) => setRegion(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UK">UK</SelectItem>
                  <SelectItem value="US">US</SelectItem>
                  <SelectItem value="Intl">International</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="genre">Genre Focus</Label>
              <Select value={genreFocus} onValueChange={(v: any) => setGenreFocus(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fiction">Fiction</SelectItem>
                  <SelectItem value="nonfiction">Nonfiction</SelectItem>
                  <SelectItem value="translation">Translation</SelectItem>
                  <SelectItem value="lifetime">Lifetime Achievement</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="link">Official Link</Label>
              <Input
                id="link"
                type="url"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="logo">Logo URL (Optional)</Label>
            <Input
              id="logo"
              type="url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://..."
            />
            <p className="text-xs text-muted-foreground">
              URL to the award's official logo or badge image. This will be displayed on award pages and book listings.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the award..."
              rows={3}
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Shortlisted Books * ({shortlistBooks.length})</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setBookSelectorOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Book
              </Button>
            </div>
            
            <BookSelector 
              open={bookSelectorOpen}
              onClose={() => setBookSelectorOpen(false)}
              onSelect={handleAddBook}
              startWithSearch={true}
            />

            {shortlistBooks.length > 0 && (
              <div className="grid gap-3 mt-4">
                {shortlistBooks.map((book, index) => (
                  <div
                    key={book.book_id}
                    className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                  >
                    <span className="text-sm font-medium text-muted-foreground w-6">
                      {index + 1}.
                    </span>
                    {book.cover_url && (
                      <img
                        src={book.cover_url}
                        alt={book.title}
                        className="w-12 h-16 object-cover rounded"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate flex items-center gap-2">
                        {book.title}
                        {book.position === 1 && (
                          <Crown className="w-4 h-4 text-yellow-500" />
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {book.authors.join(', ')}
                      </p>
                    </div>
                    <Button
                      variant={book.position === 1 ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleSetWinner(book.book_id)}
                      title={book.position === 1 ? "Winner" : "Mark as winner"}
                    >
                      <Crown className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveBook(book.book_id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            {editingAwardId && (
              <Button
                variant="outline"
                onClick={handleCancelEdit}
              >
                Cancel Edit
              </Button>
            )}
            {!editingAwardId && (
              <Button
                variant="outline"
                onClick={() => navigate('/awards')}
              >
                Back to Awards
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={saving || !prizeName || !year || shortlistBooks.length === 0}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : editingAwardId ? (
                'Update Award Shortlist'
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Award Shortlist
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
